import { useQuery } from '@tanstack/react-query';
import { createPackagePatternDraft } from '@/features/operation-package-config-new/query/draft-api';
import { getOperationPackageInstances, getOperationPackagePatterns, getOperationTaskCountInProgress, getSelfReportConfirmationFreeFormAnswers, getSelfReportFreeFormColumnInstances } from '@/features/operation-packages/api';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { QUERY_PRESETS } from '@/lib/query';
import { inHousePackageQueryKey } from './key';
import { mapApplicationInstanceToRow } from './mapApplicationInstanceToRow';
import { mapSelfReportConfirmationQuestionColumns, mapSelfReportConfirmationRow } from './mapSelfReportConfirmationRow';
import { mapSelfReportInstanceToRow, mapSelfReportQuestionColumns } from './mapSelfReportInstanceToRow';
import type { ApplicationInstanceRow, InHousePackage, InHousePackagePattern, SelfReportConfirmationQuestionColumn, SelfReportConfirmationRow, SelfReportInstanceRow, SelfReportQuestionColumn } from './type';

/** In-House パッケージの owner product（IT 運用コア） */
const IN_HOUSE_OWNER_PRODUCT = 'IT_OPERATION_CORE' as const;

/**
 * テナント専用パッケージ一覧系クエリの共通オプション。
 *
 * `QUERY_PRESETS.list()` は staleTime 5分のため、パターン公開／パッケージ完了／
 * アーカイブ後に一覧へ戻っても 5分間は fresh 扱いで再取得されず、手動リフレッシュ
 * しないと反映されない。一覧は再訪（mount）のたびに必ず最新化する。
 */
const inHouseListQueryOptions = () => ({
  ...QUERY_PRESETS.list(),
  refetchOnMount: 'always' as const
});

/**
 * BOE パッケージ種別を In-House パッケージ表示種別へマッピングする。
 * 対応外の packageType（InHouse だが未対応）は null を返し一覧から除外する。
 */
function toInHousePackage(pkg: {
  operationPackageId: string;
  packageType: string;
}): InHousePackage | null {
  switch (pkg.packageType) {
    case 'InHouseItocSelfReport':
      return {
        id: pkg.operationPackageId,
        type: 'SelfReport',
        labelKey: 'selfReport'
      };
    case 'InHouseItocApproval':
      return {
        id: pkg.operationPackageId,
        type: 'Application',
        labelKey: 'application'
      };
    default:
      return null;
  }
}

/** テナント専用パッケージ一覧（サイドバー用） */
export async function listInHousePackages(): Promise<InHousePackage[]> {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}', {
    params: {
      path: {
        ownerProduct: IN_HOUSE_OWNER_PRODUCT
      },
      query: {
        packageClass: 'InHouse'
      }
    }
  });
  return getDataOrThrowApiError(res).map(toInHousePackage).filter((pkg): pkg is InHousePackage => pkg !== null);
}

/**
 * アーカイブフィルター選択肢用: テナント専用パッケージを実パッケージ名で取得する。
 * サイドバー用 `listInHousePackages` は labelKey（種別ラベル）に丸めるため区別がつかない。
 * 複数の in-house 定義が並んでも区別できるよう、ここでは実パッケージ名（pkg.name）を返す。
 */
async function listInHousePackageOptions(): Promise<{
  id: string;
  name: string;
}[]> {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}', {
    params: {
      path: {
        ownerProduct: IN_HOUSE_OWNER_PRODUCT
      },
      query: {
        packageClass: 'InHouse'
      }
    }
  });
  // サイドバー一覧（listInHousePackages）と同様、対応済みサブタイプ（SelfReport / Application）のみを
  // 候補にする。未対応サブタイプはアーカイブ行を持たず、選んでも空一覧になり壊れて見えるため除外する。
  return getDataOrThrowApiError(res).filter(pkg => toInHousePackage(pkg) !== null).map(pkg => ({
    id: pkg.operationPackageId,
    name: pkg.name
  }));
}
export function useInHousePackageOptions() {
  return useQuery({
    queryKey: inHousePackageQueryKey.options(),
    queryFn: listInHousePackageOptions,
    ...inHouseListQueryOptions()
  });
}

/** セルフレポートのパターン一覧 */
async function listSelfReportPatterns(packageId: string): Promise<InHousePackagePattern[]> {
  const patterns = await getOperationPackagePatterns(IN_HOUSE_OWNER_PRODUCT, packageId);
  return patterns.map(pattern => ({
    operationPackagePatternId: pattern.operationPackagePatternId,
    name: pattern.name
  }));
}

/** セルフレポートのインスタンス一覧（freeForm 設問列付き・グルーピング表示用） */
async function listSelfReportInstances(args: {
  packageId: string;
  patternId: string;
  page?: number;
  pageSize?: number;
  targetId?: string;
}): Promise<{
  items: SelfReportInstanceRow[];
  questionColumns: SelfReportQuestionColumn[];
  count: number;
  maxPage: number;
  page: number;
}> {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 100;
  const response = await getSelfReportFreeFormColumnInstances(IN_HOUSE_OWNER_PRODUCT, args.patternId, {
    page,
    pageSize,
    targetId: args.targetId
  });
  const questionColumns = mapSelfReportQuestionColumns(response.questionColumns);
  return {
    items: response.items.map(mapSelfReportInstanceToRow),
    questionColumns,
    count: response.count,
    maxPage: response.maxPage,
    page: response.page
  };
}

/** セルフレポート確認画面の対象者別回答一覧 */
async function listSelfReportConfirmationRows(args: {
  patternId: string;
  page?: number;
  pageSize?: number;
  targetId?: string;
  tagId?: string;
}): Promise<{
  items: SelfReportConfirmationRow[];
  questionColumns: SelfReportConfirmationQuestionColumn[];
  count: number;
  maxPage: number;
  page: number;
}> {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 100;
  const response = await getSelfReportConfirmationFreeFormAnswers(IN_HOUSE_OWNER_PRODUCT, args.patternId, {
    page,
    pageSize,
    targetId: args.targetId,
    tagId: args.tagId
  });
  return {
    items: response.items.map(mapSelfReportConfirmationRow),
    questionColumns: mapSelfReportConfirmationQuestionColumns(response.questionColumns),
    count: response.count,
    maxPage: response.maxPage,
    page: response.page
  };
}

/** 申請パッケージのパターン一覧 */
async function listApplicationPatterns(packageId: string): Promise<InHousePackagePattern[]> {
  return listSelfReportPatterns(packageId);
}

/** 申請パッケージのインスタンス一覧 */
async function listApplicationInstances(args: {
  packageId: string;
  patternId: string;
  page?: number;
  pageSize?: number;
  targetId?: string;
}): Promise<{
  items: ApplicationInstanceRow[];
  count: number;
  maxPage: number;
  page: number;
}> {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 100;
  const response = await getOperationPackageInstances(IN_HOUSE_OWNER_PRODUCT, args.patternId, {
    page,
    pageSize,
    targetId: args.targetId
  });
  return {
    items: response.items.map(mapApplicationInstanceToRow),
    count: response.count,
    maxPage: response.maxPage,
    page: response.page
  };
}
export function useListApplicationPatterns(packageId: string) {
  return useQuery({
    queryKey: inHousePackageQueryKey.patterns(packageId),
    queryFn: () => listApplicationPatterns(packageId),
    enabled: Boolean(packageId),
    ...inHouseListQueryOptions()
  });
}
export function useListApplicationInstances(args: {
  packageId: string;
  patternId: string;
  page?: number;
  pageSize?: number;
  targetId?: string;
}) {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 100;
  return useQuery({
    queryKey: inHousePackageQueryKey.applicationInstances(args.patternId, {
      page,
      pageSize,
      targetId: args.targetId
    }),
    queryFn: () => listApplicationInstances({
      packageId: args.packageId,
      patternId: args.patternId,
      page,
      pageSize,
      targetId: args.targetId
    }),
    enabled: Boolean(args.packageId) && Boolean(args.patternId),
    ...inHouseListQueryOptions()
  });
}

/**
 * 申請パッケージのパターンを新規作成する（in-house draft 作成）。
 * POST patterns はセルフレポートと同じ DraftPattern を返す。
 */
export async function createApplicationPattern(packageId: string, values: {
  name: string;
  description?: string;
}): Promise<{
  operationPackagePatternId: string;
  name: string;
  description?: string;
  anchorDateLabel: string;
}[]> {
  return createSelfReportPattern(packageId, values);
}
export function useListSelfReportPatterns(packageId: string) {
  return useQuery({
    queryKey: inHousePackageQueryKey.patterns(packageId),
    queryFn: () => listSelfReportPatterns(packageId),
    enabled: Boolean(packageId),
    ...inHouseListQueryOptions()
  });
}
export function useListSelfReportConfirmationRows(args: {
  patternId: string;
  page?: number;
  pageSize?: number;
  targetId?: string;
  tagId?: string;
}) {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 100;
  return useQuery({
    queryKey: inHousePackageQueryKey.confirmationRows(args.patternId, {
      page,
      pageSize,
      targetId: args.targetId,
      tagId: args.tagId
    }),
    queryFn: () => listSelfReportConfirmationRows({
      patternId: args.patternId,
      page,
      pageSize,
      targetId: args.targetId,
      tagId: args.tagId
    }),
    enabled: Boolean(args.patternId),
    ...inHouseListQueryOptions()
  });
}
export function useListSelfReportInstances(args: {
  packageId: string;
  patternId: string;
  page?: number;
  pageSize?: number;
  targetId?: string;
}) {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 100;
  return useQuery({
    queryKey: inHousePackageQueryKey.instances(args.patternId, {
      page,
      pageSize,
      targetId: args.targetId
    }),
    queryFn: () => listSelfReportInstances({
      packageId: args.packageId,
      patternId: args.patternId,
      page,
      pageSize,
      targetId: args.targetId
    }),
    enabled: Boolean(args.packageId) && Boolean(args.patternId),
    ...inHouseListQueryOptions()
  });
}

/** PatternTypeTabs の進行中タスク数取得 hook */
export function useInHousePackageTaskCountInProgress(arg: {
  operationPackageId: string;
  patternId: string;
}) {
  return useQuery({
    queryKey: inHousePackageQueryKey.taskCountInProgress(arg.operationPackageId, arg.patternId),
    queryFn: () => getOperationTaskCountInProgress(IN_HOUSE_OWNER_PRODUCT, arg.operationPackageId, arg.patternId),
    enabled: Boolean(arg.operationPackageId) && Boolean(arg.patternId),
    ...inHouseListQueryOptions()
  });
}

/** @deprecated use createPackagePatternDraft from operation-package-config-new */
export async function createSelfReportPattern(packageId: string, values: {
  name: string;
  description?: string;
}) {
  return createPackagePatternDraft(IN_HOUSE_OWNER_PRODUCT, packageId, values);
}